/**
 * Version freshness for the `wayai report create` pre-flight gate (and any caller that needs to know
 * whether the running CLI / installed skill is behind the latest known release).
 *
 * Reuses the same on-disk cache the update-nudge writes (`version-cache.ts`) — so this does NO
 * network I/O — plus the installed-skill detector (`skill-version.ts`). "Behind" compares against the
 * last-known cached `latest` at ANY age (like the nudge): a cached latest can only ever be
 * equal-or-older than the true latest, so this never OVER-reports staleness — at worst it misses a
 * very recent bump and fails open. When the cache is empty (never refreshed — a cold machine), the
 * `latest` is null and every component reads as not-behind, so the gate fails open there too.
 */

import { readVersionCache, CLI_CACHE_FILE, SKILL_CACHE_FILE } from './version-cache.js';
import { findInstalledSkills, lowestInstalledVersion } from './skill-version.js';
import { findRepoRootSync } from './workspace.js';
import { isNewerVersion } from './utils.js';

export interface ComponentStatus {
  /** Running CLI / lowest installed skill version; null when unknown (CLI unreadable, skill absent). */
  current: string | null;
  /** Last-known latest from the local cache; null when never refreshed. */
  latest: string | null;
  /** current and latest both known AND current < latest. */
  behind: boolean;
}

export interface VersionStatus {
  cli: ComponentStatus;
  skill: ComponentStatus;
}

function componentStatus(current: string | null, latest: string | null): ComponentStatus {
  return { current, latest, behind: Boolean(current && latest && isNewerVersion(latest, current)) };
}

/** Resolve CLI + public-skill freshness from the local cache + installed skill files (no network). */
export function resolveVersionStatus(cliVersion: string | null): VersionStatus {
  const cliLatest = readVersionCache(CLI_CACHE_FILE)?.latest ?? null;

  // Skill freshness only matters when the public `wayai` skill is installed in this repo.
  // `lowestInstalledVersion` already returns null for an empty install list, so no length guard.
  const repoRoot = findRepoRootSync() ?? process.cwd();
  const skillCurrent = lowestInstalledVersion(findInstalledSkills(repoRoot));
  const skillLatest = skillCurrent ? (readVersionCache(SKILL_CACHE_FILE)?.latest ?? null) : null;

  return { cli: componentStatus(cliVersion, cliLatest), skill: componentStatus(skillCurrent, skillLatest) };
}

/** True when either the CLI or the installed skill is behind its latest known release. */
export function isVersionBehind(status: VersionStatus): boolean {
  return status.cli.behind || status.skill.behind;
}

/**
 * The `client_version` string stamped on a report: `cli@<v>` plus `skill@<v>` when the skill is
 * installed. Clamped to the contract's 64-char cap (`createReportBody.client_version`).
 */
export function formatClientVersion(status: VersionStatus): string {
  const cli = `cli@${status.cli.current ?? 'unknown'}`;
  if (!status.skill.current) return cli.slice(0, 64);
  // Append the skill segment only when the whole stamp still fits the cap — dropping it wholesale
  // beats truncating a version token mid-string (which would leave an unparseable `skill@6.2…`).
  const withSkill = `${cli} skill@${status.skill.current}`;
  return withSkill.length <= 64 ? withSkill : cli.slice(0, 64);
}

/**
 * A human-facing warning listing every component that's behind, or null when everything is current
 * (or freshness is unknown). Callers decide whether "behind" blocks the report or is a soft note.
 */
export function buildStaleToolingWarning(status: VersionStatus): string | null {
  const lines: string[] = [];
  if (status.cli.behind) {
    lines.push(`  CLI    ${status.cli.current} → ${status.cli.latest}   (run: wayai update)`);
  }
  if (status.skill.behind) {
    lines.push(`  Skill  ${status.skill.current} → ${status.skill.latest}   (run: npx skills add wayai-pro/wayai-skill -y)`);
  }
  if (lines.length === 0) return null;
  return [
    'Your WayAI tooling is behind the latest release:',
    ...lines,
    '',
    'A stale CLI or skill is the most common cause of a false bug report — the issue may',
    'already be fixed upstream. Update, then reproduce before reporting.',
  ].join('\n');
}
